import { useEffect, useRef, useState } from 'preact/hooks';
import type { Bucket } from '../../shared/types';
import { apiPost, ApiError } from '../lib/api';
import {
  PositionLegsEditor,
  emptyLeg,
  parseLegDrafts,
  type LegDraft,
} from './PositionLegsEditor';
import './../styles/components/edit-popover.css';

const ASSET_CLASSES = [
  { value: '', label: '—' },
  { value: 'equity', label: 'Equity' },
  { value: 'crypto_volatile', label: 'Crypto' },
  { value: 'bond', label: 'Bond' },
  { value: 'cash_eq', label: 'Cash' },
  { value: 'gold', label: 'Gold' },
] as const;

type Props = {
  buckets: Bucket[];
  defaultBucketId?: string | null;
  onClose: () => void;
  onSaved: () => void;
};

export function AddHoldingDialog({ buckets, defaultBucketId, onClose, onSaved }: Props) {
  const sorted = [...buckets].sort((a, b) => a.name.localeCompare(b.name));
  const [bucketId, setBucketId] = useState(
    defaultBucketId && sorted.some((b) => b.id === defaultBucketId)
      ? defaultBucketId
      : (sorted[0]?.id ?? '')
  );
  const [symbol, setSymbol] = useState('');
  const [name, setName] = useState('');
  const [providerSymbol, setProviderSymbol] = useState('');
  const [risk, setRisk] = useState('5');
  const [assetClass, setAssetClass] = useState('');
  const [ccy, setCcy] = useState('USD');
  const [geo, setGeo] = useState('');
  const [legs, setLegs] = useState<LegDraft[]>(() => [emptyLeg()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const isStablecoin = assetClass === 'cash_eq';

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (saving) return;
    setError(null);

    const sym = symbol.trim().toUpperCase();
    const nm = name.trim();
    if (!bucketId) {
      setError('Pick a bucket');
      return;
    }
    if (!sym) {
      setError('Symbol is required');
      return;
    }
    if (!nm) {
      setError('Name is required');
      return;
    }

    const riskN = Number(risk);
    if (!Number.isInteger(riskN) || riskN < 1 || riskN > 10) {
      setError('Risk must be an integer from 1 to 10');
      return;
    }

    const body: Record<string, unknown> = {
      bucket_id: bucketId,
      symbol: sym,
      name: nm,
      risk: riskN,
    };

    const ps = providerSymbol.trim();
    if (ps) body.provider_symbol = ps;

    if (assetClass) body.asset_class = assetClass;
    if (ccy.trim()) body.ccy = ccy.trim().toUpperCase();
    if (geo.trim()) body.geo = geo.trim();

    const hasAnyQty = legs.some((l) => l.quantity.trim() !== '');
    if (hasAnyQty) {
      const parsed = parseLegDrafts(legs, { cashEq: isStablecoin });
      if ('error' in parsed) {
        setError(parsed.error);
        return;
      }
      body.positions = parsed.legs;
    }

    setSaving(true);
    try {
      await apiPost('/api/portfolio/holdings', body);
      onSaved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Create failed';
      setError(msg || 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <dialog
      ref={dialogRef}
      class="edit-modal edit-modal-wide"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialogRef.current) dialogRef.current?.close();
      }}
    >
      <form class="edit-pop" onSubmit={handleSubmit}>
        <h3 class="edit-pop-title">
          Add holding
          <span class="edit-pop-subtitle">
            index / merge_group can be set later via SQL if needed
          </span>
        </h3>

        <div class="edit-pop-grid">
          <div class="edit-pop-row">
            <label class="edit-pop-label">Bucket</label>
            <select
              class="edit-pop-input"
              value={bucketId}
              onChange={(e) => setBucketId((e.target as HTMLSelectElement).value)}
              disabled={saving}
              required
            >
              {sorted.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div class="edit-pop-row">
            <label class="edit-pop-label">Symbol</label>
            <input
              class="edit-pop-input mono"
              value={symbol}
              onInput={(e) => setSymbol((e.target as HTMLInputElement).value)}
              disabled={saving}
              required
              autoComplete="off"
            />
          </div>

          <div class="edit-pop-row edit-pop-span-2">
            <label class="edit-pop-label">Name</label>
            <input
              class="edit-pop-input"
              value={name}
              onInput={(e) => setName((e.target as HTMLInputElement).value)}
              disabled={saving}
              required
            />
          </div>

          <div class="edit-pop-row">
            <label class="edit-pop-label">Yahoo symbol</label>
            <input
              class="edit-pop-input mono"
              placeholder="defaults to symbol"
              value={providerSymbol}
              onInput={(e) => setProviderSymbol((e.target as HTMLInputElement).value)}
              disabled={saving}
              autoComplete="off"
            />
          </div>

          <div class="edit-pop-row">
            <label class="edit-pop-label">Risk (1–10)</label>
            <input
              class="edit-pop-input mono"
              type="number"
              min="1"
              max="10"
              step="1"
              value={risk}
              onInput={(e) => setRisk((e.target as HTMLInputElement).value)}
              disabled={saving}
              required
            />
          </div>

          <div class="edit-pop-row">
            <label class="edit-pop-label">Asset class</label>
            <select
              class="edit-pop-input"
              value={assetClass}
              onChange={(e) => setAssetClass((e.target as HTMLSelectElement).value)}
              disabled={saving}
            >
              {ASSET_CLASSES.map((c) => (
                <option key={c.value || 'none'} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div class="edit-pop-row">
            <label class="edit-pop-label">Currency</label>
            <input
              class="edit-pop-input mono"
              value={ccy}
              onInput={(e) => setCcy((e.target as HTMLInputElement).value)}
              disabled={saving}
              autoComplete="off"
            />
          </div>

          <div class="edit-pop-row">
            <label class="edit-pop-label">Geo</label>
            <input
              class="edit-pop-input mono"
              placeholder="US, EM, …"
              value={geo}
              onInput={(e) => setGeo((e.target as HTMLInputElement).value)}
              disabled={saving}
              autoComplete="off"
            />
          </div>
        </div>

        <div class="edit-pop-row edit-pop-span-2" style={{ marginTop: '4px' }}>
          <PositionLegsEditor
            legs={legs}
            cashEq={isStablecoin}
            ccyLabel={ccy.trim() || undefined}
            saving={saving}
            onChange={setLegs}
          />
        </div>

        {error && <div class="edit-pop-error mono">{error}</div>}
        <div class="edit-pop-actions">
          <button
            type="button"
            class="edit-pop-btn edit-pop-btn-ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button type="submit" class="edit-pop-btn edit-pop-btn-primary" disabled={saving}>
            {saving ? 'Adding…' : 'Add'}
          </button>
        </div>
      </form>
    </dialog>
  );
}
